import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseInput from 'components/atoms/base-input';
import BaseButton from 'components/atoms/baseButton';
import DraggableModal from 'components/organisms/draggableModal';
import { useFormik } from 'formik';
import {
  useCreateCampaign,
  useUpdateCampaign,
} from 'infrastructure/hooks/sms-campaigns';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';
import QuestionKeySelectBox from './question-key-select-box';
import { createCampaignSchema, updateCampaignSchema } from './schema';

import type {
  ICreateSmsCampaign,
  IUpdateSmsCampaign,
} from 'infrastructure/interfaces';
import type { FC } from 'react';

type AddEditCampaignFormProps =
  | {
      open: boolean;
      toggleOpen: () => void;
      isEditMode: true;
      defaultValues: IUpdateSmsCampaign;
    }
  | {
      open: boolean;
      toggleOpen: () => void;
      isEditMode?: false;
      defaultValues?: never;
    };

const AddEditCampaignForm: FC<AddEditCampaignFormProps> = ({
  open,
  toggleOpen,
  isEditMode,
  defaultValues,
}) => {
  const { createCampaign, isLoading: createLoading } = useCreateCampaign();
  const { updateCampaign, isLoading: updateLoading } = useUpdateCampaign();
  const { t } = useTranslation();

  const initialValues: IUpdateSmsCampaign | ICreateSmsCampaign = isEditMode
    ? defaultValues
    : { name: '', questionKey: '' };

  const formik = useFormik<typeof initialValues>({
    initialValues,
    onSubmit: (data) => {
      if (isEditMode && 'guid' in data) {
        updateCampaign(data, {
          onSuccess: () => {
            onClose();
          },
        });
      }
      if (!isEditMode) {
        createCampaign(data, {
          onSuccess: () => {
            onClose();
          },
        });
      }
    },
    enableReinitialize: true,
    validationSchema: isEditMode
      ? updateCampaignSchema()
      : createCampaignSchema(),
  });
  const loading = createLoading || updateLoading;
  const modalTitle = isEditMode
    ? t('smsCampaign.editCampaign')
    : t('smsCampaign.addNewSmsCampaign');

  const onSave = async () => {
    await formik.submitForm();
  };

  const onClose = () => {
    toggleOpen();
    formik.resetForm();
  };

  return (
    <DraggableModal
      isOpen={open}
      toggle={onClose}
      title={modalTitle}
      bodyClassName={s['modal-body']}
      modalClassName={s.modal}
      footer={
        <div className={s['modal-footer']}>
          <BaseButton
            type="secondary"
            onClick={() => {
              onClose();
            }}
            label={t('controls.cancel')}
          />
          <BaseButton
            type="primary"
            onClick={onSave}
            label={
              isEditMode
                ? t('smsCampaign.saveChanges')
                : t('smsCampaign.addCampaign')
            }
            disabled={loading}
            loading={loading}
          />
        </div>
      }
    >
      <BaseForm formik={formik}>
        <div className={s.wrapper}>
          <BaseFormItem
            name="name"
            label={t('smsCampaign.campaignName')}
            required
          >
            <BaseInput />
          </BaseFormItem>
          <BaseFormItem
            name="questionKey"
            label={t('smsCampaign.campaignQuestionListSource')}
            required={!isEditMode}
          >
            <QuestionKeySelectBox disabled={isEditMode} />
          </BaseFormItem>
        </div>
      </BaseForm>
    </DraggableModal>
  );
};

export default AddEditCampaignForm;
